export default function ProfileReducer(
  state: any,
  action: {
    type: string;
    [key: string]: any;
  }
) {
  const defaultState = {
    notables: {
      feeds: [],
      loaded: false,
      loadMoreButton: false
    },
    subjects: {
      posts: [],
      loaded: false,
      loadMoreButton: false
    },
    pinnedAICards: {
      cardIds: [],
      loaded: false
    },
    pinnedBuilds: {
      builds: [],
      favoriteViewerId: 0,
      loaded: false,
      isTopBuilds: false,
      expanded: false
    },
    likes: {
      ['ai-stories']: [],
      all: [],
      allLoaded: false,
      allLoadMoreButton: false,
      allByUser: [],
      allByUserLoaded: false,
      allByUserLoadMoreButton: false,
      comments: [],
      commentsLoaded: false,
      commentsLoadMoreButton: false,
      reflections: [],
      reflectionsLoaded: false,
      reflectionsLoadMoreButton: false,
      subjects: [],
      subjectsLoaded: false,
      subjectsLoadMoreButton: false,
      subjectsByUser: [],
      subjectsByUserLoaded: false,
      subjectsByUserLoadMoreButton: false,
      videos: [],
      videosLoaded: false,
      videosLoadMoreButton: false,
      videosByUser: [],
      videosByUserLoaded: false,
      videosByUserLoadMoreButton: false,
      links: [],
      linksLoaded: false,
      linksLoadMoreButton: false,
      linksByUser: [],
      linksByUserLoaded: false,
      linksByUserLoadMoreButton: false
    },
    posts: {
      bounties: [],
      bountiesLoaded: false,
      bountiesLoadMoreButton: false,
      ['ai-stories']: [],
      all: [],
      allLoaded: false,
      allLoadMoreButton: false,
      allByUser: [],
      allByUserLoaded: false,
      allByUserLoadMoreButton: false,
      comments: [],
      commentsLoaded: false,
      commentsLoadMoreButton: false,
      reflections: [],
      reflectionsLoaded: false,
      reflectionsLoadMoreButton: false,
      subjects: [],
      subjectsLoaded: false,
      subjectsLoadMoreButton: false,
      subjectsByUser: [],
      subjectsByUserLoaded: false,
      subjectsByUserLoadMoreButton: false,
      videos: [],
      videosLoaded: false,
      videosLoadMoreButton: false,
      videosByUser: [],
      videosByUserLoaded: false,
      videosByUserLoadMoreButton: false,
      watched: [],
      watchedLoaded: false,
      watchedLoadMoreButton: false,
      links: [],
      linksLoaded: false,
      linksLoadMoreButton: false,
      linksByUser: [],
      linksByUserLoaded: false,
      linksByUserLoadMoreButton: false
    }
  };
  const username = action.username;
  const prevContentState = state[action.username] || defaultState;
  switch (action.type) {
    case 'RESET_PROFILE_VIEWER_STATE': {
      const nextState: Record<string, any> = {};
      for (const profileUsername of Object.keys(state)) {
        const profileState = state[profileUsername] || defaultState;
        nextState[profileUsername] = {
          ...profileState,
          // Noteworthy rows carry requester-specific subject-secret fields.
          // They must never survive an account switch.
          notables: {
            ...profileState.notables,
            feeds: [],
            loaded: false,
            loadMoreButton: false
          },
          subjects: {
            ...profileState.subjects,
            posts: [],
            loaded: false,
            loadMoreButton: false
          },
          posts: { ...defaultState.posts },
          likes: { ...defaultState.likes }
        };
      }
      return nextState;
    }
    case 'LOAD_FEATURED_SUBJECTS':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          subjects: {
            ...prevContentState.subjects,
            posts: action.subjects,
            loaded: true
          }
        }
      };
    case 'LOAD_NOTABLES':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          notables: {
            ...prevContentState.notables,
            feeds: action.feeds,
            loadMoreButton: action.loadMoreButton,
            loaded: true
          }
        }
      };
    case 'LOAD_PINNED_AI_CARDS':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          pinnedAICards: {
            ...prevContentState.pinnedAICards,
            cardIds: action.cardIds,
            loaded: true
          }
        }
      };
    case 'LOAD_PINNED_BUILDS':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          pinnedBuilds: {
            ...prevContentState.pinnedBuilds,
            builds: action.builds,
            favoriteViewerId:
              Number(action.favoriteViewerId) > 0
                ? Number(action.favoriteViewerId)
                : 0,
            loaded: true,
            isTopBuilds: Boolean(action.isTopBuilds)
          }
        }
      };
    case 'LOAD_MORE_NOTABLES':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          notables: {
            ...prevContentState.notables,
            feeds: prevContentState.notables.feeds.concat(action.feeds),
            loadMoreButton: action.loadMoreButton
          }
        }
      };
    case 'LOAD_POSTS':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          posts: {
            ...prevContentState.posts,
            [action.section]: action.feeds,
            [`${action.section}Loaded`]: true,
            [`${action.section}LoadMoreButton`]: action.loadMoreButton
          }
        }
      };
    case 'LOAD_LIKED_POSTS':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          likes: {
            ...prevContentState.likes,
            [action.section]: action.feeds,
            [`${action.section}Loaded`]: true,
            [`${action.section}LoadMoreButton`]: action.loadMoreButton
          }
        }
      };
    case 'LOAD_POSTS_BY_USER':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          posts: {
            ...prevContentState.posts,
            [`${action.section}ByUser`]: action.feeds,
            [`${action.section}ByUserLoaded`]: true,
            [`${action.section}ByUserLoadMoreButton`]: action.loadMoreButton
          }
        }
      };
    case 'LOAD_MORE_POSTS':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          posts: {
            ...prevContentState.posts,
            [action.section]: [
              ...prevContentState.posts[action.section],
              ...action.feeds.filter(
                (newFeed: any) =>
                  !prevContentState.posts[action.section].some(
                    (existingFeed: any) =>
                      existingFeed.feedId === newFeed.feedId
                  )
              )
            ],
            [`${action.section}LoadMoreButton`]: action.loadMoreButton
          }
        }
      };
    case 'LOAD_MORE_LIKED_POSTS':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          likes: {
            ...prevContentState.likes,
            [action.section]: [
              ...prevContentState.likes[action.section],
              ...action.feeds.filter(
                (newFeed: any) =>
                  !prevContentState.likes[action.section].some(
                    (existingFeed: any) =>
                      existingFeed.feedId === newFeed.feedId
                  )
              )
            ],
            [`${action.section}LoadMoreButton`]: action.loadMoreButton
          }
        }
      };

    case 'LOAD_MORE_POSTS_BY_USER':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          posts: {
            ...prevContentState.posts,
            [`${action.section}ByUser`]: [
              ...prevContentState.posts[`${action.section}ByUser`],
              ...action.feeds.filter(
                (newFeed: any) =>
                  !prevContentState.posts[`${action.section}ByUser`].some(
                    (existingFeed: any) =>
                      existingFeed.feedId === newFeed.feedId
                  )
              )
            ],
            [`${action.section}ByUserLoadMoreButton`]: action.loadMoreButton
          }
        }
      };
    case 'RESET_PROFILE':
      return {
        ...state,
        [username]: {
          ...defaultState,
          profileId: state.profileId
        }
      };
    case 'SET_PROFILE_ID':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          profileId: action.profileId
        }
      };
    case 'SET_FEATURED_SUBJECTS':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          subjects: {
            ...prevContentState.subjects,
            posts: action.subjects
          }
        }
      };
    case 'SET_PINNED_AI_CARDS':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          pinnedAICards: {
            ...prevContentState.pinnedAICards,
            cardIds: action.cardIds,
            loaded: true
          }
        }
      };
    case 'SET_PINNED_BUILDS_EXPANDED':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          pinnedBuilds: {
            ...prevContentState.pinnedBuilds,
            expanded: Boolean(action.expanded)
          }
        }
      };
    case 'SET_PINNED_BUILDS':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          pinnedBuilds: {
            ...prevContentState.pinnedBuilds,
            builds: action.builds,
            favoriteViewerId:
              Number(action.favoriteViewerId) > 0
                ? Number(action.favoriteViewerId)
                : 0,
            loaded: true,
            isTopBuilds: Boolean(action.isTopBuilds)
          }
        }
      };
    case 'USER_NOT_EXIST':
      return {
        ...state,
        [username]: {
          ...prevContentState,
          notExist: true
        }
      };
    default:
      return state;
  }
}
